// Data hook for the admin-gated `/admin/keys` page: every stored Anthropic key,
// an assignment, a removal and a monthly limit
// (`apps/server/src/http/anthropic-key.ts`).
// Mirrors useAccess.ts: fetch on mount, and a 403 is the denied state. In an
// env without the org's admin key the list reads `assignable: false`: keys
// are listed and removed there, never assigned. A write answers the line for
// its closed code, never the body, and the list is read again after it
// either way.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  AdminAnthropicKeysResponseSchema,
  AnthropicKeyStatusSchema,
  type AdminAnthropicKeyRow,
  type AnthropicKeyOwner,
  type AssignAnthropicKeyBody,
  type SetAnthropicKeyLimitBody,
  type UnassignAnthropicKeyBody,
} from "@loom/shared/admin";

import { keyFailureCopy } from "../account/anthropicKeyCopy";
import type { MessageKey } from "../i18n/index.js";
import { HttpError, UnauthorizedError, httpGet, httpPost } from "../lib/http";

const keyFailureOf = keyFailureCopy([
  ["key_reserved", "admin.keys.error.reserved"],
  ["key_not_in_org", "admin.keys.error.notInOrg"],
  ["key_ambiguous", "admin.keys.error.ambiguous"],
  ["key_not_assignable", "admin.keys.error.notAssignable"],
  ["key_no_workspace", "admin.keys.error.noWorkspace"],
  ["key_taken", "admin.keys.error.taken"],
  ["key_assigned", "admin.keys.error.assigned"],
  ["key_deactivate_failed", "admin.keys.error.deactivateFailed"],
  ["review_session", "admin.keys.error.reviewSession"],
]);

const KEYS_PATH = "/api/admin/anthropic-keys";

export type AnthropicKeysLoad = "loading" | "ready" | "forbidden" | "failed";

export interface AnthropicKeysAdminState {
  readonly keys: ReadonlyArray<AdminAnthropicKeyRow>;
  readonly load: AnthropicKeysLoad;
  /** False where no key is assigned (no org admin key). */
  readonly assignable: boolean;
  /** Null once done, else the line that says why not. */
  readonly assign: (owner: AnthropicKeyOwner, key: string) => Promise<MessageKey | null>;
  readonly unassign: (owner: AnthropicKeyOwner) => Promise<MessageKey | null>;
  /** Null clears the limit. */
  readonly setLimit: (
    owner: AnthropicKeyOwner,
    monthlyLimitUsd: string | null,
  ) => Promise<MessageKey | null>;
}

export const useAnthropicKeysAdmin = (): AnthropicKeysAdminState => {
  const [keys, setKeys] = useState<ReadonlyArray<AdminAnthropicKeyRow>>([]);
  const [load, setLoad] = useState<AnthropicKeysLoad>("loading");
  const [assignable, setAssignable] = useState(false);
  const aborted = useRef(false);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const value = await httpGet(KEYS_PATH, AdminAnthropicKeysResponseSchema);
      if (aborted.current) return;
      setKeys(value.keys);
      setAssignable(value.assignable);
      setLoad("ready");
    } catch (err) {
      // A lapsed sign-in goes to /sign-in; keep loading, don't flash.
      if (aborted.current || err instanceof UnauthorizedError) return;
      setLoad(err instanceof HttpError && err.status === 403 ? "forbidden" : "failed");
    }
  }, []);

  useEffect(() => {
    aborted.current = false;
    void reload();
    return () => {
      aborted.current = true;
    };
  }, [reload]);

  const write = useCallback(
    async (path: string, body: unknown, fallback: MessageKey): Promise<MessageKey | null> => {
      let line: MessageKey | null = null;
      try {
        await httpPost(path, body, AnthropicKeyStatusSchema);
      } catch (err) {
        line = keyFailureOf(err, fallback);
      }
      await reload();
      return line;
    },
    [reload],
  );

  const assign = useCallback(
    (owner: AnthropicKeyOwner, key: string) =>
      write(KEYS_PATH, { owner, key } satisfies AssignAnthropicKeyBody, "admin.keys.assignFailed"),
    [write],
  );

  const unassign = useCallback(
    (owner: AnthropicKeyOwner) =>
      write(
        `${KEYS_PATH}/delete`,
        { owner } satisfies UnassignAnthropicKeyBody,
        "admin.keys.removeFailed",
      ),
    [write],
  );

  const setLimit = useCallback(
    (owner: AnthropicKeyOwner, monthlyLimitUsd: string | null) =>
      write(
        `${KEYS_PATH}/limit`,
        { owner, monthlyLimitUsd } satisfies SetAnthropicKeyLimitBody,
        "admin.keys.limitFailed",
      ),
    [write],
  );

  return { keys, load, assignable, assign, unassign, setLimit };
};
